import { Ban, FileSymlink, PencilLine } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { usePermission } from '@/hooks/usePermission';
import type { DocumentStatus } from '@/lib/document-status';

import { AnnulModal } from './AnnulModal';
import { CorrectCodeModal } from './CorrectCodeModal';
import { LinkLmsModal } from './LinkLmsModal';

// Panel de la ficha para un documento recién dado de alta (solo Calidad): mientras
// está reservado se corrige el código o se anula la reserva; mientras está reservado
// o en borrador se vincula (o cambia) su archivo del LMS. Desde revisión, ni el
// código ni el archivo cambian, así que el panel no aparece. Excepción: un vigente sin
// archivo (carga histórica sin par en el LMS) lo vincula para poder modificarse.

const LINKABLE: ReadonlySet<DocumentStatus> = new Set(['reservado', 'borrador']);

type Dialog = 'code' | 'annul' | 'link' | null;

export interface RegistrationEntry {
  id: string;
  code: string;
  status: DocumentStatus;
  lmsLinked: boolean;
}

export function RegistrationPanel({
  entry,
  creatingInLms = false,
}: {
  entry: RegistrationEntry;
  /** El LMS está creando su documento desde la plantilla (la ficha se refresca sola). */
  creatingInLms?: boolean;
}): ReactElement | null {
  const canRegister = usePermission('quality:document:register');
  const [dialog, setDialog] = useState<Dialog>(null);
  const vigenteWithoutFile = entry.status === 'vigente' && !entry.lmsLinked;
  if (!canRegister || (!LINKABLE.has(entry.status) && !vigenteWithoutFile)) {
    return null;
  }
  const reserved = entry.status === 'reservado';
  const close = (): void => setDialog(null);

  return (
    <div className="card rise d1">
      <div className="ch">
        <h3>Reserva y archivo</h3>
      </div>
      <div className="cb" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {creatingInLms && !entry.lmsLinked ? (
          // Recorrido 2026-11-04: en curso no es un error; no se ofrece vincular a mano.
          <p className="soft" role="status" style={{ margin: 0, fontSize: 13 }}>
            Creando el documento en el LMS desde la plantilla…
          </p>
        ) : (
          <>
            <p className="soft" style={{ margin: 0, fontSize: 13 }}>
              Archivo del LMS:{' '}
              {entry.lmsLinked ? (
                <strong>Vinculado</strong>
              ) : (
                <strong>Sin vincular. La edición en línea necesita el archivo del LMS.</strong>
              )}
            </p>
            <button type="button" className="btn" onClick={() => setDialog('link')}>
              <FileSymlink size={16} aria-hidden="true" />
              {entry.lmsLinked ? 'Cambiar archivo del LMS' : 'Vincular archivo del LMS'}
            </button>
          </>
        )}
        {reserved && (
          <>
            <button type="button" className="btn mut" onClick={() => setDialog('code')}>
              <PencilLine size={16} aria-hidden="true" />
              Corregir código
            </button>
            <button type="button" className="btn danger" onClick={() => setDialog('annul')}>
              <Ban size={16} aria-hidden="true" />
              Anular reserva
            </button>
          </>
        )}
      </div>
      {dialog === 'code' && (
        <CorrectCodeModal entryId={entry.id} code={entry.code} onClose={close} />
      )}
      {dialog === 'annul' && <AnnulModal entryId={entry.id} code={entry.code} onClose={close} />}
      {dialog === 'link' && <LinkLmsModal entryId={entry.id} code={entry.code} onClose={close} />}
    </div>
  );
}
